import React, { useState, useRef, useEffect } from 'react';
import { ChatMessage } from '../components/chat/ChatMessage';
import { ChatInput } from '../components/chat/ChatInput';
import { motion } from 'framer-motion';
import { getChatCompletion } from '../services/openai';
import { AlertCircle, Lock } from 'lucide-react';
import { useAuthContext } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';

interface Message {
  id: string;
  content: string;
  isUser: boolean;
}

const INITIAL_MESSAGE = "Hi! I'm your CRO Assistant. I can help you improve your website's conversion rates through better copywriting. What would you like to know?";

const SYSTEM_PROMPT = "You're a professional marketing copywriter who knows how to increase conversion rates through good copywriting.";

export function ChatPage() {
  const { user, profile } = useAuthContext();
  const navigate = useNavigate();
  const [messages, setMessages] = useState<Message[]>([
    { id: '0', content: INITIAL_MESSAGE, isUser: false }
  ]);
  const [isTyping, setIsTyping] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const isPro = profile?.subscription?.tier === 'PRO';

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSend = async (content: string) => {
    if (!isPro) return;

    setError(null);
    const userMessage: Message = {
      id: Date.now().toString(),
      content,
      isUser: true
    };
    setMessages(prev => [...prev, userMessage]);
    setIsTyping(true);

    try {
      const chatMessages = [
        { role: 'system' as const, content: SYSTEM_PROMPT },
        ...messages.map(msg => ({
          role: msg.isUser ? 'user' as const : 'assistant' as const,
          content: msg.content
        })),
        { role: 'user' as const, content }
      ];

      const response = await getChatCompletion(chatMessages);
      
      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        content: response,
        isUser: false
      };
      setMessages(prev => [...prev, assistantMessage]);
    } catch (err) {
      setError('Failed to get response. Please try again.');
      console.error('Chat error:', err);
    } finally {
      setIsTyping(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen pt-32 pb-16 px-4">
        <div className="max-w-md mx-auto text-center">
          <Lock className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-4">Sign in to Access Chat</h1>
          <p className="text-gray-600 mb-8">
            Please sign in to use the CRO Assistant Chat feature.
          </p>
          <Button onClick={() => navigate('/pricing')}>
            Sign In
          </Button>
        </div>
      </div>
    );
  }

  if (!isPro) {
    return (
      <div className="min-h-screen pt-32 pb-16 px-4">
        <div className="max-w-md mx-auto text-center">
          <Lock className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-4">Pro Subscription Required</h1>
          <p className="text-gray-600 mb-8">
            Upgrade to Pro to access the CRO Assistant Chat and get personalized conversion optimization advice.
          </p>
          <Button onClick={() => navigate('/pricing')}>
            Upgrade to Pro
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 pb-6 px-4">
      <div className="max-w-4xl mx-auto h-[calc(100vh-8rem)] flex flex-col">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-lg p-6 flex-1 overflow-y-auto mb-6"
        >
          <div className="space-y-6">
            {messages.map((message) => (
              <ChatMessage
                key={message.id}
                message={message.content}
                isUser={message.isUser}
              />
            ))}
            {isTyping && (
              <ChatMessage
                message=""
                isUser={false}
                isTyping={true}
              />
            )}
            {error && (
              <div className="flex items-center gap-2 text-red-500 p-4 bg-red-50 rounded-lg">
                <AlertCircle className="w-5 h-5" />
                <p>{error}</p>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        </motion.div>
        <ChatInput onSend={handleSend} disabled={isTyping} />
      </div>
    </div>
  );
}